import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ErrorBoundary,
  ErrorReload,
  Iframe,
  Loader,
  PHISH_URLS,
  useAuth0,
  useLSPhishing,
  getPhishingEmbedUrl,
} from '@livingsecurity/shared';

import { AuthorizedLayout } from 'components';

const PhishingDashboard = ({ history }) => {
  const { locale } = useAuth0();
  const { loading, hasAccess, getEmbeddedUrl } = useLSPhishing();
  const [iframeLoading, setLoading] = useState(true);
  const [hasError, setError] = useState(false);
  const [isAuthLoaded, setIsAuthLoaded] = useState(false);
  const authUrl = getEmbeddedUrl();

  // Extract resourceId from URL if present
  const resourceId = window.location.pathname.match(/\/campaign-report\/([^/]+)/)?.[1];
  const campaignReportUrl = resourceId
    ? `${getPhishingEmbedUrl()}/reports/campaign-reports/campaign-report/${resourceId}/1`
    : null;

  useEffect(() => {
    if (window.location?.href?.includes('livingsecurity.com')) {
      window.document.domain = 'livingsecurity.com';
    }
  }, []);

  useEffect(() => {
    if (!loading && !iframeLoading && !hasAccess && window.location?.pathname !== PHISH_URLS.demo) {
      history.replace(PHISH_URLS.demo);
    }
  }, [hasAccess, loading, history, iframeLoading]);

  const onAuthLoad = useCallback(() => {
    setIsAuthLoaded(true);
  }, []);

  const onLoad = useCallback(() => {
    if (isAuthLoaded) {
      setTimeout(() => {
        setLoading(false);
      }, 2000);
    }
  }, [isAuthLoaded]);

  const onError = useCallback((error) => {
    /* eslint no-console: "off" */
    console.error(error);
    setError(true);
  }, []);

  const refreshPage = useCallback(() => {
    window.location.reload(true);
  }, []);

  if (!locale) {
    return null;
  }

  return (
    <AuthorizedLayout app="PHISHING" withBottomPaddingForHubspot>
      <>
        {!hasError && (loading || !isAuthLoaded) && <Loader />}
        {hasError ? (
          <ErrorReload callback={refreshPage} />
        ) : (
          <>
            {/* Hidden auth iframe */}
            {((!isAuthLoaded && authUrl) || (!resourceId && authUrl)) && (
              <Iframe
                id="phish-auth-frame"
                data-testid="phish-auth-frame"
                title="Phishing Auth"
                src={authUrl}
                onLoad={onAuthLoad}
                style={{ display: resourceId ? 'none' : 'block' }}
              />
            )}
            {isAuthLoaded && campaignReportUrl && resourceId && (
              <ErrorBoundary scope="phishing-dashboard">
                <Iframe
                  id="phish-embed-frame"
                  allow="clipboard-read; clipboard-write *;"
                  className="embedded-iframe"
                  data-testid="phish-embed-frame"
                  title="Living Security Phish"
                  src={campaignReportUrl}
                  onLoad={onLoad}
                  onError={onError}
                  style={{
                    opacity: `${iframeLoading ? 0 : 1}`,
                    transition: 'opacity 1s',
                  }}
                />
              </ErrorBoundary>
            )}
          </>
        )}
      </>
    </AuthorizedLayout>
  );
};

PhishingDashboard.propTypes = {
  history: PropTypes.any.isRequired,
};

export default PhishingDashboard;
